// exclusionPreview — 「人を外して戻す」のブラウザ replay プレビュー
// (vandalism-recovery-ui-2026-09-27.md §3 流れ 3)。
//
// 既存のタイムラプス replay (`createTimelapseFrameSource` = 本番 snapshot と同じ
// drawing-core) を、系譜の範囲リストで「最終フレームまで」回して 1 枚返す。
// `previewFrame.ts` の 'end' は進捗を出せないので、ここでは少数 (PROGRESS_FRAMES)
// の中間フレームを pull して `onProgress` を呼ぶ (最後のフレーム = 全イベント適用後)。
//
// サーバ (焼き) との既知の小さな差:
//   - 土台: サーバは「外す線より前の最後の snapshot 世代」から焼く。ここでは
//     系譜の根から全部 replay する (結果が同じになるのが前提。snapshot は replay と
//     ビット一致を確認済み — growth-timelapse-share §5.3.2)。
//   - 取り消し (`stroke.cancelled`): サーバは取り消し済み sid を除いて焼く。
//     ここは live と同じ「bbox ローカルの巻き戻し」で再生する。重なる後続ストローク
//     が無い取り消しだけがサーバで許されるので、通常は同じ絵になる。
//   - 貼り付け画像: 縮小補間 (Canvas2D / Skia) の差、再署名できなかった画像は
//     skippedImagepastes として数える (サーバ側にも別途 skip が起き得る)。
//   - マスク: 外した線の上にマスクで描いた他人の線は、当時と違う結果になる
//     (サーバも同じ — プレビューで判断する)。

import type { StrokeLogEvent } from '@oekaki/protocol';
import type { FrameRgba, FrameSize } from './frameScale.js';
import type { SeqRange } from './segmentMerge.js';
import { createTimelapseFrameSource, type ResolveImagepasteFn } from './timelapseReplay.js';

/** 進捗表示のために pull する中間フレーム数 (多いほど滑らかだが composite が増える)。 */
export const PROGRESS_FRAMES = 12;

export interface ExclusionPreviewResult {
  readonly width: number;
  readonly height: number;
  readonly rgba: FrameRgba;
  readonly totalImagepastes: number;
  readonly skippedImagepastes: number;
}

export interface RenderExclusionPreviewInput {
  /** 外す対象を引いた後のイベント列 (`filterExcludedEvents`)。 */
  readonly events: readonly StrokeLogEvent[];
  readonly canvas: FrameSize;
  /** 系譜の範囲リスト (`collectFinalDocumentRanges`、外す前の列から求めたもの)。 */
  readonly seqRanges: readonly SeqRange[];
  readonly maxEdge: number;
  /**
   * 初期背景 (終端の meta の色)。絵ログを系譜の根までしか読まないので、全消し前の
   * `background-set` はログに無い。文書内の background-set は再生されるため、初期値 =
   * 終端の背景色で最終フレームは正しくなる。省略時は `backgroundAt(events)` (従来)。
   */
  readonly background?: { readonly rgb: number; readonly alpha: number };
  readonly resolveImagepaste?: ResolveImagepasteFn;
  /** 0..1。呼ぶ頻度は中間フレームごと。 */
  readonly onProgress?: (fraction: number) => void;
  readonly signal?: AbortSignal;
}

/** 中断されたとき / 範囲が空のとき `null`。 */
export const renderExclusionPreview = async (
  input: RenderExclusionPreviewInput,
): Promise<ExclusionPreviewResult | null> => {
  const aborted = (): boolean => input.signal?.aborted === true;
  if (input.seqRanges.length === 0 || aborted()) return null;
  const source = createTimelapseFrameSource({
    events: input.events,
    canvas: input.canvas,
    seqRanges: input.seqRanges,
    mode: { kind: 'duration', seconds: PROGRESS_FRAMES / 30 },
    fps: 30,
    maxEdge: input.maxEdge,
    ...(input.background !== undefined ? { background: input.background } : {}),
    ...(input.resolveImagepaste !== undefined
      ? { resolveImagepaste: input.resolveImagepaste }
      : {}),
  });
  const total = source.plan.frameCount;
  const gen = source.frames();
  let last: { width: number; height: number; rgba: FrameRgba } | null = null;
  try {
    for (;;) {
      const next = await gen.next();
      if (aborted()) return null;
      if (next.done) {
        if (last === null) return null;
        return {
          ...last,
          totalImagepastes: next.value.totalImagepastes,
          skippedImagepastes: next.value.skippedImagepastes,
        };
      }
      last = { width: next.value.width, height: next.value.height, rgba: next.value.rgba };
      input.onProgress?.(Math.min(1, (next.value.index + 1) / Math.max(1, total)));
    }
  } finally {
    await gen.return?.(undefined as never);
  }
};
